import { useState, useEffect } from 'react'
import { useNavigate, useSearchParams, Link } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import { useToast } from '../components/Toast'
import { supabase } from '../lib/supabase'

export default function AuthPage() {
  const [searchParams] = useSearchParams()
  const [mode, setMode] = useState(() => searchParams.get('mode') === 'reset' ? 'reset' : 'signin')
  const [newPassword, setNewPassword] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [displayName, setDisplayName] = useState('')
  const [loading, setLoading] = useState(false)
  const { signIn, signUp, signInWithGoogle, user, loading: authLoading } = useAuth()
  const toast = useToast()
  const navigate = useNavigate()

  // Redirect once user is confirmed — fires both on page load (already signed in)
  // and after a successful sign-in (onAuthStateChange sets user)
  useEffect(() => {
    if (!authLoading && user) navigate('/dashboard')
  }, [authLoading, user])

  // While initial auth check is running, show nothing to avoid flash
  // This only lasts <100ms on a normal connection
  if (authLoading) return (
    <div style={{ minHeight: '100vh', background: 'var(--paper)' }} />
  )

  async function handleSubmit(e) {
    e.preventDefault()
    setLoading(true)

    if (mode === 'signin') {
      const { error } = await signIn(email, password)
      if (error) { toast(error.message, 'error'); setLoading(false) }
      // on success: useEffect above handles redirect once user is set

    } else if (mode === 'signup') {
      if (!displayName.trim()) { toast('Please enter a display name.', 'error'); setLoading(false); return }
      const { error } = await signUp(email, password, displayName)
      if (error) { toast(error.message, 'error'); setLoading(false) }
      else { setMode('confirm') }

    } else if (mode === 'forgot') {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/auth?mode=reset`,
      })
      if (error) { toast(error.message, 'error'); setLoading(false) }
      else { toast('Password reset email sent.', 'success'); setMode('signin') }

    } else if (mode === 'reset') {
      if (!newPassword || newPassword.length < 6) {
        toast('Password must be at least 6 characters.', 'error'); setLoading(false); return
      }
      const { error } = await supabase.auth.updateUser({ password: newPassword })
      if (error) { toast(error.message, 'error'); setLoading(false) }
      else { toast('Password updated. You are now signed in.', 'success'); navigate('/dashboard') }
    }
    setLoading(false)
  }

  async function handleGoogle() {
    const { error } = await signInWithGoogle()
    if (error) toast(error.message, 'error')
  }

  // Password reset screen
  if (mode === 'reset') {
    return (
      <div style={styles.centerPage}>
        <div style={styles.confirmBox}>
          <div style={styles.confirmIcon}>🔑</div>
          <p style={styles.eyebrow}>reset password</p>
          <h2 style={styles.confirmTitle}>Set a new password</h2>
          <form onSubmit={handleSubmit} style={{ marginTop: '1.5rem', width: '100%' }}>
            <div className="form-group">
              <label className="form-label">New Password</label>
              <input className="form-input" type="password" value={newPassword}
                onChange={e => setNewPassword(e.target.value)}
                placeholder="At least 6 characters" required minLength={6} autoFocus />
            </div>
            <button type="submit" className="btn btn-ink" style={{ width: '100%', justifyContent: 'center', marginTop: '0.5rem' }} disabled={loading}>
              {loading ? 'Updating...' : 'Update Password'}
            </button>
          </form>
        </div>
      </div>
    )
  }

  // Email confirmation screen
  if (mode === 'confirm') {
    return (
      <div style={styles.centerPage}>
        <div style={styles.confirmBox}>
          <div style={styles.confirmIcon}>✉</div>
          <p style={styles.eyebrow}>almost there</p>
          <h2 style={styles.confirmTitle}>Check your email</h2>
          <p style={styles.confirmText}>
            We sent a confirmation link to <strong style={{ color: 'var(--ink)' }}>{email}</strong>.
            Click the link to activate your account, then come back to sign in.
          </p>
          <button className="btn btn-ink" style={{ marginTop: '1.5rem' }} onClick={() => setMode('signin')}>
            Back to Sign In
          </button>
          <p style={styles.confirmResend}>
            Didn't get it?{' '}
            <button
              style={styles.textBtn}
              onClick={async () => {
                await supabase.auth.resend({ type: 'signup', email })
                toast('Confirmation email resent.', 'success')
              }}
            >
              Resend
            </button>
          </p>
        </div>
      </div>
    )
  }

  return (
    <div style={styles.page} className="auth-page">
      <div style={styles.left} className="auth-left">
        <Link to="/" style={styles.backLink}>← the margins</Link>
        <div style={styles.hero}>
          <p style={styles.eyebrow}>the margins</p>
          <h1 style={styles.heroTitle}>
            {mode === 'signin' ? 'welcome back.' : mode === 'forgot' ? 'reset password.' : 'say something.'}
          </h1>
          <p style={styles.heroSub}>
            {mode === 'signin'
              ? 'your perspective is still here. pick up where you left off.'
              : mode === 'forgot'
              ? 'enter your email and we\'ll send you a reset link.'
              : 'sign up and start writing. no approval process, no waiting list. just your argument and somewhere it actually lands.'}
          </p>
          <blockquote style={styles.quote}>"the place for everything you never said."</blockquote>
        </div>
      </div>

      <div style={styles.right} className="auth-right">
        <div style={styles.formBox}>
          {mode !== 'forgot' && (
            <div style={styles.tabs}>
              <button style={{ ...styles.tab, ...(mode === 'signin' ? styles.tabActive : {}) }} onClick={() => setMode('signin')}>Sign In</button>
              <button style={{ ...styles.tab, ...(mode === 'signup' ? styles.tabActive : {}) }} onClick={() => setMode('signup')}>Create Account</button>
            </div>
          )}

          {mode === 'forgot' && (
            <div style={{ marginBottom: '1.5rem' }}>
              <h2 style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: '1.4rem', fontWeight: 700, marginBottom: '0.5rem' }}>
                Forgot your password?
              </h2>
              <p style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: '0.85rem', color: 'var(--muted)', lineHeight: 1.6 }}>
                Enter your email and we'll send you a link to reset it.
              </p>
            </div>
          )}

          <form onSubmit={handleSubmit}>
            {mode === 'signup' && (
              <div className="form-group">
                <label className="form-label">Display Name</label>
                <input className="form-input" value={displayName} onChange={e => setDisplayName(e.target.value)} placeholder="How should we call you?" required />
              </div>
            )}
            <div className="form-group">
              <label className="form-label">Email Address</label>
              <input className="form-input" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@email.com" required />
            </div>
            {mode !== 'forgot' && (
              <div className="form-group">
                <label className="form-label">Password</label>
                <input className="form-input" type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="••••••••" required minLength={6} />
              </div>
            )}

            {mode === 'signin' && (
              <div style={{ textAlign: 'right', marginTop: '-0.75rem', marginBottom: '1rem' }}>
                <button type="button" style={styles.textBtn} onClick={() => setMode('forgot')}>
                  Forgot password?
                </button>
              </div>
            )}

            <button type="submit" className="btn btn-ink" style={{ width: '100%', justifyContent: 'center', marginBottom: '1rem' }} disabled={loading}>
              {loading ? 'Please wait...' : mode === 'signin' ? 'Sign In' : mode === 'forgot' ? 'Send Reset Link' : 'Start Writing'}
            </button>
          </form>

          {mode === 'forgot' && (
            <p style={{ textAlign: 'center', marginTop: '1rem' }}>
              <button style={styles.textBtn} onClick={() => setMode('signin')}>← Back to sign in</button>
            </p>
          )}

          {mode !== 'forgot' && (
            <>
              <div style={styles.orDivider}><span>or</span></div>
              <button onClick={handleGoogle} style={styles.googleBtn}>
                <svg width="18" height="18" viewBox="0 0 18 18">
                  <path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.875 2.684-6.615z"/>
                  <path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z"/>
                  <path fill="#FBBC05" d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z"/>
                  <path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z"/>
                </svg>
                Continue with Google
              </button>
            </>
          )}

          <p style={styles.switchText}>
            {mode === 'signin'
              ? <span>New to The Margins? <button style={styles.textBtn} onClick={() => setMode('signup')}>Create an account</button></span>
              : mode === 'signup'
              ? <span>Already have an account? <button style={styles.textBtn} onClick={() => setMode('signin')}>Sign in</button></span>
              : null}
          </p>
        </div>
      </div>
    </div>
  )
}

const styles = {
  page: { minHeight: '100vh', display: 'grid', gridTemplateColumns: '1fr 1fr', paddingTop: 0 },
  left: {
    background: 'var(--ink)', color: 'var(--paper)',
    padding: '3rem', display: 'flex', flexDirection: 'column',
    position: 'sticky', top: 0, height: '100vh',
  },
  backLink: { fontFamily: "'DM Mono', monospace", fontSize: '0.68rem', letterSpacing: '0.1em', textTransform: 'uppercase', color: '#888', marginBottom: 'auto', display: 'block' },
  hero: { marginBottom: '3rem' },
  eyebrow: { fontFamily: "'DM Mono', monospace", fontSize: '0.65rem', letterSpacing: '0.18em', textTransform: 'uppercase', color: 'var(--accent)', marginBottom: '1.2rem' },
  heroTitle: { fontFamily: "'Space Grotesk', sans-serif", fontSize: 'clamp(3rem, 6vw, 5rem)', fontWeight: 700, lineHeight: 0.95, letterSpacing: '-0.03em', marginBottom: '1.5rem' },
  heroSub: { fontSize: '1rem', lineHeight: 1.75, color: '#a09890', maxWidth: '38ch' },
  quote: { marginTop: '2rem', fontFamily: "'Space Grotesk', sans-serif", fontStyle: 'italic', fontSize: '1.1rem', lineHeight: 1.6, color: '#888', borderLeft: '2px solid var(--accent)', paddingLeft: '1.2rem' },
  right: { display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '3rem 2rem', background: 'var(--cream)' },
  formBox: { width: '100%', maxWidth: 420 },
  tabs: { display: 'flex', marginBottom: '2rem', borderBottom: '1px solid var(--rule)' },
  tab: { flex: 1, padding: '0.75rem', background: 'none', border: 'none', fontFamily: "'DM Mono', monospace", fontSize: '0.7rem', letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--muted)', borderBottom: '2px solid transparent', marginBottom: '-1px', transition: 'all 0.2s', cursor: 'pointer' },
  tabActive: { color: 'var(--ink)', borderBottomColor: 'var(--ink)' },
  orDivider: { textAlign: 'center', margin: '1.25rem 0', fontFamily: "'DM Mono', monospace", fontSize: '0.65rem', color: 'var(--muted)' },
  googleBtn: { width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.75rem', padding: '0.8rem', background: 'white', border: '1px solid #ddd', fontFamily: "'DM Mono', monospace", fontSize: '0.7rem', letterSpacing: '0.06em', color: '#1a1a1a', transition: 'border-color 0.2s', cursor: 'pointer', borderRadius: 6 },
  switchText: { marginTop: '1.5rem', textAlign: 'center', fontFamily: "'DM Mono', monospace", fontSize: '0.65rem', color: 'var(--muted)' },
  textBtn: { background: 'none', border: 'none', color: 'var(--accent)', fontFamily: "'DM Mono', monospace", fontSize: '0.65rem', textDecoration: 'underline', cursor: 'pointer', padding: 0 },
  // Confirm screen
  centerPage: { minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '2rem', background: 'var(--cream)' },
  confirmBox: { textAlign: 'center', maxWidth: 440, padding: '3rem 2.5rem', background: 'var(--paper)', border: '1px solid var(--rule)', borderRadius: 12 },
  confirmIcon: { fontSize: '3rem', marginBottom: '1rem' },
  confirmTitle: { fontFamily: "'Space Grotesk', sans-serif", fontSize: '1.6rem', fontWeight: 700, marginBottom: '1rem' },
  confirmText: { fontFamily: "'Space Grotesk', sans-serif", fontSize: '0.9rem', color: 'var(--muted)', lineHeight: 1.7 },
  confirmResend: { marginTop: '1.5rem', fontFamily: "'DM Mono', monospace", fontSize: '0.65rem', color: 'var(--muted)' },
}
